import { Component, OnInit, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  KnowledgeApiService,
  KnowledgeChunkRecord,
  KnowledgeDocumentRecord,
  KnowledgeRetrievalLogRecord,
  KnowledgeSourceHealthRecord,
  KnowledgeSourceRecord
} from './knowledge-api.service';

@Component({
  standalone: true,
  selector: 'ain-knowledge-source-detail',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule, TextareaModule, JalaliDateInputComponent],
  template: `
    <ain-page [title]="t('knowledge.sourceDetail.title')" [description]="t('knowledge.sourceDetail.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!source()) {
        <ain-state type="empty" [title]="t('knowledge.source.notFound')" />
      } @else {
        <section class="panel">
          <h3>{{ source()!.sourceType }} · {{ source()!.status }}</h3>
          @if (health()) {
            <p>{{ t('knowledge.sourceDetail.health') }}: {{ health()!.activeDocumentCount }} docs / {{ health()!.chunkCount }} chunks
              @if (health()!.isStale) { · <strong>{{ t('knowledge.sourceDetail.stale') }}</strong> }</p>
          }
          <div class="actions">
            <button pButton type="button" size="small" [label]="t('knowledge.sourceDetail.activate')" (click)="activate()"></button>
            <button pButton type="button" size="small" severity="secondary" [label]="t('knowledge.sourceDetail.deactivate')" (click)="deactivate()"></button>
            <button pButton type="button" size="small" severity="help" [label]="t('knowledge.sourceDetail.reingest')" [disabled]="reingesting()" (click)="reingest()"></button>
          </div>
        </section>

        <section class="panel">
          <h3>{{ t('knowledge.sourceDetail.ingestTitle') }}</h3>
          <div class="grid">
            <label><span>{{ t('knowledge.documents.fields.title') }}</span><input pInputText [(ngModel)]="ingestForm.title" /></label>
            <label><span>{{ t('knowledge.documents.fields.uri') }}</span><input pInputText [(ngModel)]="ingestForm.uri" /></label>
            <label><span>{{ t('knowledge.documents.fields.content') }}</span><textarea pTextarea rows="4" [(ngModel)]="ingestForm.content"></textarea></label>
            <label><span>{{ t('knowledge.documents.fields.effectiveTo') }}</span><ain-date-input [(ngModel)]="ingestForm.effectiveToUtc" /></label>
          </div>
          <button pButton type="button" [label]="t('knowledge.sourceDetail.ingest')" [disabled]="ingesting()" (click)="ingest()"></button>
        </section>

        <section class="panel">
          <h3>{{ t('knowledge.sourceDetail.retrievalTest') }}</h3>
          <div class="grid">
            <label><span>{{ t('knowledge.retrieval.fields.query') }}</span><input pInputText [(ngModel)]="retrievalQuery" /></label>
          </div>
          <button pButton type="button" severity="secondary" [label]="t('knowledge.sourceDetail.runRetrieval')" [disabled]="retrieving()" (click)="runRetrieval()"></button>
          @if (retrievalHits().length) {
            <ul class="hits">
              @for (hit of retrievalHits(); track hit.chunkId) {
                <li>{{ hit.citation.documentTitle }} ({{ hit.citation.chunkIndex }}) — {{ hit.text }}</li>
              }
            </ul>
          }
        </section>

        <section class="panel">
          <h3>{{ t('knowledge.documents.title') }}</h3>
          <p-table [value]="documents()" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('knowledge.documents.fields.title') }}</th>
                <th>{{ t('columns.version') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.title }}</td>
                <td>{{ row.version }}</td>
                <td>{{ row.status }}</td>
                <td><button pButton type="button" size="small" [label]="t('knowledge.chunks.preview')" (click)="previewChunks(row)"></button></td>
              </tr>
            </ng-template>
          </p-table>
          @if (chunks().length) {
            <h4>{{ t('knowledge.chunks.title') }}</h4>
            <p-table [value]="chunks()" dataKey="id">
              <ng-template pTemplate="header">
                <tr>
                  <th>#</th>
                  <th>{{ t('knowledge.chunks.sensitivity') }}</th>
                  <th>{{ t('knowledge.chunks.text') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.chunkIndex }}</td>
                  <td>{{ row.sensitivityLabel }}</td>
                  <td>{{ row.text }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        <section class="panel">
          <h3>{{ t('knowledge.retrievalLogs.title') }}</h3>
          <p-table [value]="logs()" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('knowledge.retrieval.fields.query') }}</th>
                <th>{{ t('knowledge.retrievalLogs.consumer') }}</th>
                <th>{{ t('columns.createdAt') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.query }}</td>
                <td>{{ row.consumerModule }}</td>
                <td>{{ row.requestedAtUtc }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 14px; background: #fff; }
    .grid { display: grid; gap: 10px; margin-bottom: 10px; }
    label { display: grid; gap: 6px; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
    .hits { margin: 10px 0 0; padding-inline-start: 18px; font-size: 13px; }
  `]
})
export class KnowledgeSourceDetailComponent implements OnInit {
  private readonly api = inject(KnowledgeApiService);
  private readonly localization = inject(LocalizationService);

  readonly sourceId = input.required<string>();

  readonly loading = signal(true);
  readonly ingesting = signal(false);
  readonly reingesting = signal(false);
  readonly retrieving = signal(false);
  readonly source = signal<KnowledgeSourceRecord | null>(null);
  readonly health = signal<KnowledgeSourceHealthRecord | null>(null);
  readonly documents = signal<KnowledgeDocumentRecord[]>([]);
  readonly chunks = signal<KnowledgeChunkRecord[]>([]);
  readonly logs = signal<KnowledgeRetrievalLogRecord[]>([]);
  readonly retrievalHits = signal<Array<{ chunkId: string; text: string; citation: { documentTitle: string; chunkIndex: number } }>>([]);

  retrievalQuery = '';
  ingestForm = {
    title: '',
    uri: '',
    content: '',
    effectiveToUtc: null as string | null
  };

  ngOnInit(): void { this.load(); }

  load(): void {
    const id = this.sourceId();
    this.loading.set(true);
    this.api.getSource(id).subscribe({
      next: source => {
        this.source.set(source);
        this.loading.set(false);
        this.api.getSourceHealth(id).subscribe({ next: health => this.health.set(health) });
        this.api.listDocuments(id).subscribe({ next: docs => this.documents.set(docs) });
        this.api.listRetrievalLogs().subscribe({ next: logs => this.logs.set(logs) });
      },
      error: () => this.loading.set(false)
    });
  }

  activate(): void { this.api.activateSource(this.sourceId()).subscribe({ next: () => this.load() }); }
  deactivate(): void { this.api.deactivateSource(this.sourceId()).subscribe({ next: () => this.load() }); }

  reingest(): void {
    this.reingesting.set(true);
    this.api.reingestSource(this.sourceId()).subscribe({
      next: () => { this.reingesting.set(false); this.load(); },
      error: () => this.reingesting.set(false)
    });
  }

  ingest(): void {
    this.ingesting.set(true);
    this.api.ingestDocument(this.sourceId(), {
      title: this.ingestForm.title,
      uri: this.ingestForm.uri,
      content: this.ingestForm.content,
      effectiveFromUtc: null,
      effectiveToUtc: this.ingestForm.effectiveToUtc
    }).subscribe({
      next: () => { this.ingesting.set(false); this.load(); },
      error: () => this.ingesting.set(false)
    });
  }

  previewChunks(document: KnowledgeDocumentRecord): void {
    this.api.listChunks(document.id).subscribe({ next: rows => this.chunks.set(rows) });
  }

  runRetrieval(): void {
    this.retrieving.set(true);
    this.api.retrieve({ query: this.retrievalQuery, topK: 5, consumerModule: 'knowledge-hub', filters: {} }).subscribe({
      next: result => {
        this.retrievalHits.set(result.items.map(item => ({
          chunkId: item.chunkId,
          text: item.text,
          citation: { documentTitle: item.citation.documentTitle, chunkIndex: item.citation.chunkIndex }
        })));
        this.retrieving.set(false);
        this.api.listRetrievalLogs().subscribe({ next: logs => this.logs.set(logs) });
      },
      error: () => this.retrieving.set(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
